/* @flow */
import * as React from 'react';

// import invariant from 'invariant';
import idx from 'idx';
import { graphql } from 'react-apollo';
import SettingsStore from 'stores/SettingsStore';
import ListActions from 'components/ListActions';
import TextWithIcon from 'components/TextWithIcon.jsx';
import Image from 'components/Image.jsx';
import type {
  ListActionsFilterState,
  ListActionsCheckboxFilterState,
  ListActionsSelectFilterState,
} from '../ListActions/ListActions.jsx';
import MediaObject from 'components/MediaObject.jsx';
import adminAvatarImage from './images/roles/admin.svg';
import noRoleAvatarImage from './images/roles/no-role.svg';
import parentAvatarImage from './images/roles/parent.svg';
import studentAvatarImage from './images/roles/student.svg';
import teacherAvatarImage from './images/roles/teacher.svg';
import type { OrgRole } from './queries/__generated__/GetOrganizationMembers';
import type { Props as TextWithIconProps } from 'components/TextWithIcon.jsx';
import type { UserRole } from './PeopleList.jsx';

import GET_ORG_ROLE_COUNTS_QUERY from './queries/OrganizationRoleCountsQuery.gql';

function PeopleListRosterLabel(props: $Shape<TextWithIconProps>) {
  return <TextWithIcon {...props} align="left" append gutter="xxsmall" iconSize="large" inline />;
}

export type PeopleListFilterState = {
  role: UserRole,
  source?: ListActionsCheckboxFilterState,
  grade?: ListActionsSelectFilterState,
};

type RoleInfo = {
  avatar: string,
  count: number,
  label: string,
  value: OrgRole,
};

type Props = {
  entityUuid: string,
  query: ?string,
  filters: PeopleListFilterState,
  onFiltersChange: (activeFilters: ListActionsFilterState) => void,
  onQueryChange: (query: string) => void,
  roles: RoleInfo[],
  loading: boolean,
  error: errorFragmentFragment,
  reloadQuery: boolean,
  refetch: () => void,
};

class PeopleListFilterUIDataContainer extends React.Component<Props, *> {
  componentWillReceiveProps(nextProps: Props) {
    const { refetch } = this.props;
    if (nextProps.reloadQuery) {
      refetch();
    }
  }

  constructRoleListOptions = (roles: RoleInfo[]) => {
    return roles.map(item => ({
      ...item,
      display: (
        <MediaObject center gutter="xsmall" inline className="PeopleList-actions-label">
          <Image src={item.avatar} />
          <div>
            {item.label}
            {item.count !== -1 && (
              <span>
                &nbsp;(
                {item.count})
              </span>
            )}
          </div>
        </MediaObject>
      ),
      value: item.value,
    }));
  };

  render() {
    const { onFiltersChange, onQueryChange, filters, roles } = this.props;

    const grades = SettingsStore.getSetting('grades').map(item => item.name);
    const roleListOptions = this.constructRoleListOptions(roles);

    const rosteredLabel = (
      <PeopleListRosterLabel
        icon="link-linked"
        iconColor="shamrock"
        className="PeopleList-actions-label">
        SIS-linked
      </PeopleListRosterLabel>
    );

    const notRosteredLabel = (
      <PeopleListRosterLabel
        icon="link-unlinked"
        iconColor="koala"
        className="PeopleList-actions-label">
        Not SIS-linked
      </PeopleListRosterLabel>
    );

    const selectedOption = roleListOptions.find(item => item.value === filters['role']);

    return (
      <ListActions
        filterButtonFace={selectedOption && selectedOption.display}
        initialActiveFilters={((filters: any): ListActionsFilterState)}
        primaryFilters={[
          {
            inputProps: {
              nonTextLabel: true,
            },
            name: 'role',
            options: roleListOptions,
            title: 'Role',
            type: 'radio',
          },
        ]}
        secondaryFilters={[
          {
            name: 'source',
            options: new Map([['rostered', rosteredLabel], ['unrostered', notRosteredLabel]]),
            title: 'Source',
            type: 'checkbox',
          },
          {
            inputProps: {
              items: grades,
              placeholder: '––',
              className: 'PeopleList-grade',
            },
            name: 'grade',
            title: 'Grade',
            type: 'select',
          },
        ]}
        onFilterChange={onFiltersChange}
        onQueryChange={onQueryChange}
      />
    );
  }
}

const withRoleCounts = graphql(GET_ORG_ROLE_COUNTS_QUERY, {
  options: ({ entityUuid }) => {
    return {
      variables: {
        orgUuid: entityUuid,
      },
    };
  },
  props: ({ data }) => {
    const roleCounts = idx(data.organizations, _ => _[0].roleCounts);
    const roles = [
      {
        avatar: teacherAvatarImage,
        count: -1,
        label: 'Teachers',
        value: 'teacher',
      },
      {
        avatar: studentAvatarImage,
        count: -1,
        label: 'Students',
        value: 'student',
      },
      {
        avatar: parentAvatarImage,
        count: -1,
        label: 'Parents',
        value: 'parent',
      },
      {
        avatar: adminAvatarImage,
        count: -1,
        label: 'Admins',
        value: 'admin',
      },
      {
        avatar: noRoleAvatarImage,
        count: -1,
        label: 'No role',
        value: 'unknown',
      },
    ];

    if (!roleCounts) {
      return {
        roles,
        loading: data.loading,
        error: data.error,
      };
    }

    const rolesWithCounts = roles.map(role => {
      const matchingRole = roleCounts.find(item => item.role === role.value);

      if (!matchingRole) {
        return role;
      }
      // TODO: ensure the endpoint is always returning all the roles, even if not there
      // invariant(matchingRole, "can't find matching role");

      return { ...role, count: matchingRole.count };
    });

    return {
      roles: rolesWithCounts,
      loading: data.loading,
      error: data.error,
      refetch: data.refetch,
    };
  },
});

export default withRoleCounts(PeopleListFilterUIDataContainer);
